import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import pageTitle from "../../utils/pageTitle";
import TextSearch from "../../components/search/TextSearch";
import GoogleMap from "../../components/google/GoogleMap";
import Marker from "../../components/google/Marker";
import InfoWindowSimple from "../../components/google/InfoWindowSimple";
import {
  LOCATION_GOOGLE_PLACE_ID_CHECK_URL,
  GOOGLE_PLACE_DETAILS_URL,
  GOOGLE_PLACE_ID_FINDER_URL,
} from "../../config";
import { showErrorAlert, showInfoAlert } from "../../store/actions/alerts";
import ResultGrid from "../../components/location/ResultGrid";
import KeyValueText from "../../components/display/KeyValueText";
import NullChip from "../../components/chips/NullChip";
import AddEditLocForm from "../../components/location/AddEditLocForm";

import makeApiRequest from "../../utils/axios/new-axios";
import { permissions, types, checkAcl } from "../../utils/acl";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: "auto",
    marginTop: theme.spacing(1),
  },
  paperSubmit: {
    padding: theme.spacing(2),
    margin: "auto",
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
  },
  paperTable: {
    width: "100%",
    overflowX: "auto",
    marginBottom: theme.spacing(2),
    backgroundColor: "#212121",
  },
}));

export default function AddLocation() {
  pageTitle("Add Location");
  const dispatch = useDispatch();
  const classes = useStyles();
  const EDIT_ACCESS = checkAcl({ p: permissions.LOCATION_MANAGEMENT, t: types.VIEW_AND_EDIT });

  const [isLoading, setIsLoading] = useState(false);
  const [mapDetails, setMapdetails] = useState(null);
  const [location, setLocation] = useState(null);
  const [geoCodingDetails, setGeoCodingDetails] = useState(null);
  const [admAreas, setAdmAreas] = useState(null);
  const [addrComp, setAddrComp] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const [values, setValues] = useState(null);

  useEffect(() => {
    if (geoCodingDetails && addrComp) {
      const postalCode = addrComp.find((item) => {
        return item.type === "postal_code";
      });

      setValues({
        name: !!geoCodingDetails.name ? geoCodingDetails.name : "",
        lat: !!geoCodingDetails.geometry
          ? geoCodingDetails.geometry.location.lat
          : "",
        lng: !!geoCodingDetails.geometry
          ? geoCodingDetails.geometry.location.lng
          : "",
        caption: "",
        admArea: "",
        postalCode: !!postalCode ? postalCode.longName : "",
        address: !!geoCodingDetails.formatted_address
          ? geoCodingDetails.formatted_address
          : "",
        link: "",
        googlePlaceId: !!geoCodingDetails.place_id
          ? geoCodingDetails.place_id
          : "",
        category: [],
        description: "",
        meta: {
          title: "",
          description: ""
        },
        thingsToDo: "",
        history: "",
        bestTimeToVisit: "",
        tips: "",
        facts: "",
        timeSpend: "",
        howToReach: "",
        fee: "",
        openingHours: "",
        status: 2, //pending verification (admin user created)
        creationType: 1, //google place id search
        createdThrough: 1, //admin web
      });
    }
  }, [geoCodingDetails, addrComp]); //run only when admDetail changes

  function handleOpenAddModal() {
    setOpenAddModal(true);
  }

  function handleCloseAddModal() {
    setOpenAddModal(false);
  }

  async function handleSearch(searchText) {
    setIsLoading(true);
    setGeoCodingDetails(null);
    setMapdetails(null);
    setLocation(null);
    setAdmAreas(null);
    setAddrComp(null);

    await makeApiRequest({
      url: `${LOCATION_GOOGLE_PLACE_ID_CHECK_URL.url}/${searchText}`,
      method: LOCATION_GOOGLE_PLACE_ID_CHECK_URL.method,
    })
      .then((response) => {
        if (response.status === 200) {
          dispatch(showInfoAlert("Location Exists!"));
          setLocation(response.data);
          setMapdetails({
            coordinates: response.data.coordinates,
            infoWindowTitle: response.data.name,
            infoWindowDetail: response.data.address,
          });

          setIsLoading(false);
        } else {
          dispatch(showErrorAlert("Something went wrong while fetching"));
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (error.response.status === 404) {
          //call google place details API
          getGooglePlaceDetails(searchText);
        } else {
          dispatch(
            showErrorAlert(
              "Error Checking Google Place ID : " +
              (!!error.response
                ? `${error.response.status}! - ${error.response.data.errorMessage}`
                : error.message)
            )
          );
          setIsLoading(false);
        }
      });
  }

  async function getGooglePlaceDetails(searchText) {
    setIsLoading(true);
    await makeApiRequest({
      url: `${GOOGLE_PLACE_DETAILS_URL.url}`,
      method: GOOGLE_PLACE_DETAILS_URL.method,
      params: {
        placeid: searchText,
        fields:
          "address_component,formatted_address,geometry,name,place_id,type",
      },
    })
      .then((response) => {
        //console.log(response.data);
        if (response.data.status === "OK") {
          const result = response.data.result;
          setGeoCodingDetails(result);
          //console.log(result);
          setMapdetails({
            coordinates: result.geometry.location,
            infoWindowTitle: result.name,
            infoWindowDetail: result.formatted_address,
          });
          setAdmAreas({ ...getAdminArea(result.address_components) });
          setAddrComp([...formatedAddrComps(result.address_components)]);
        } else if (response.data.status === "INVALID_REQUEST") {
          dispatch(showErrorAlert("Invalid Google Place ID"));
        } else {
          dispatch(
            showErrorAlert(
              `Google API Error : ${response.data.status}` +
              (!!response.data.error_message &&
                ` - ${response.data.error_message}`)
            )
          );
        }
      })
      .catch((error) => {
        dispatch(
          showErrorAlert(
            "Error Google Place Details API : " +
            (!!error.response
              ? `${error.response.status}! - ${error.response.data.errorMessage}`
              : error.message)
          )
        );
      });

    setIsLoading(false);
  }

  function getAdminArea(addressComponents) {
    let country = null;
    let countryIsoCode = null;
    let adm1 = null;
    let adm2 = null;
    let adm3 = null;

    addressComponents.forEach((component) => {
      if (component.types[0] === "country") {
        country = component.long_name;
        countryIsoCode = component.short_name;
      } else if (component.types[0] === "administrative_area_level_1") {
        adm1 = component.long_name;
      } else if (component.types[0] === "administrative_area_level_2") {
        adm2 = component.long_name;
      } else if (component.types[0] === "administrative_area_level_3") {
        adm3 = component.long_name;
      }
    });

    return {
      country,
      countryIsoCode,
      adm1,
      adm2,
      adm3,
    };
  }

  function formatedAddrComps(addressComponents) {
    let formatted = [];
    addressComponents.forEach((component) => {
      formatted.push({
        type: component.types[0],
        subType: component.types[1] !== "political" ? component.types[1] : null,
        longName: component.long_name,
        shortName: component.short_name,
      });
    });
    return formatted;
  }

  return (
    <Container style={{ padding: "10px" }}>
      <h3>
        Add Location (
        <Link
          color="inherit"
          target="_blank"
          href={GOOGLE_PLACE_ID_FINDER_URL}
          underline="none"
        >
          Google Place ID Finder
        </Link>
        )
      </h3>

      <TextSearch
        label="Search By Google Place ID"
        handleSearch={handleSearch}
        isSubmitting={isLoading}
      />
      {!isLoading && location && <ResultGrid location={location} />}

      {geoCodingDetails && (
        <Paper className={classes.paper}>
          <Typography gutterBottom variant="subtitle1">
            {geoCodingDetails.name}
          </Typography>
          <KeyValueText
            keyName="Address"
            value={geoCodingDetails.formatted_address}
          />
          <KeyValueText
            keyName="Coordinates"
            value={`${geoCodingDetails.geometry.location.lat}, ${geoCodingDetails.geometry.location.lng}`}
          />
          <KeyValueText
            keyName="Google Place ID"
            value={geoCodingDetails.place_id}
          />
          {admAreas && (
            <Fragment>
              <Divider style={{ margin: "10px" }} />
              <Typography variant="caption" display="block" gutterBottom>
                Administrative Areas
              </Typography>
              <KeyValueText keyName="Country" value={admAreas.country} />
              <KeyValueText
                keyName="Administrative Area Level 1"
                value={admAreas.adm1}
              />
              <KeyValueText
                keyName="Administrative Area Level 2"
                value={admAreas.adm2}
              />
              <KeyValueText
                keyName="Administrative Area Level 3"
                value={admAreas.adm3}
              />
            </Fragment>
          )}
          {!!geoCodingDetails.types && (
            <Fragment>
              <Divider style={{ margin: "10px" }} />
              <Typography variant="caption" display="block" gutterBottom>
                Geo Types
              </Typography>
              {geoCodingDetails.types.map((type) => {
                return (
                  <Chip
                    key={type}
                    size="small"
                    label={type}
                    style={{ marginRight: "10px", marginBottom: "10px" }}
                  />
                );
              })}
            </Fragment>
          )}
          {!!addrComp && (
            <Fragment>
              <Divider style={{ margin: "10px" }} />
              <Typography variant="caption" display="block" gutterBottom>
                Address Components
              </Typography>
              <Paper className={classes.paperTable}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Sub Type</TableCell>
                      <TableCell>Long Name</TableCell>
                      <TableCell>Short Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addrComp.map((component, index) => {
                      return (
                        <TableRow key={`geo-type-${index}`}>
                          <TableCell>{component.type}</TableCell>
                          <TableCell>
                            {!!component.subType ? (
                              component.subType
                            ) : (
                              <NullChip />
                            )}
                          </TableCell>
                          <TableCell>{component.longName}</TableCell>
                          <TableCell>{component.shortName}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Fragment>
          )}
        </Paper>
      )}

      {!!mapDetails && !isLoading && (
        <Paper className={classes.paper}>
          <Fragment>
            {!!showMap ? (
              <GoogleMap
                id="google-map"
                width="100%"
                height="300px"
                options={{
                  center: mapDetails.coordinates,
                  zoom: 12,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker
                  id="marker"
                  position={mapDetails.coordinates}
                  infoWindow={
                    <InfoWindowSimple
                      title={mapDetails.infoWindowTitle}
                      detail={mapDetails.infoWindowDetail}
                    />
                  }
                  defaultOpen
                />
              </GoogleMap>
            ) : (
              <Box display="flex" flexDirection="row" justifyContent="center">
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => setShowMap(true)}
                >
                  Show Map
                </Button>
              </Box>
            )}
          </Fragment>
        </Paper>
      )}
      {EDIT_ACCESS && geoCodingDetails && (
        <Fragment>
          <Paper className={classes.paperSubmit}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenAddModal}
            >
              Add Location
            </Button>
          </Paper>
          <AddEditLocForm
            action="add"
            openModal={openAddModal}
            handleCloseModal={handleCloseAddModal}
            initialValues={values}
            geoDetails={addrComp}
            geoTypes={geoCodingDetails.types}
          />
        </Fragment>
      )}
    </Container>
  );
}
